import React, { useMemo } from "react";
import { Box, useTheme } from "@mui/material";

import AiSectionFeedback from "../../Components/AiSectionFeedback";

import TalkingPointsCard from "./TalkingPointsCard";
import UpcomingMeetingsCard from "./UpcomingMeetingsCard";
import ConsultantIntelligenceScatterCard from "./ConsultantIntelligenceScatterCard";
import InteractionTimelineCard from "./InteractionTimelineCard";
import ConsultantCommentaryCard from "./ConsultantCommentaryCard";
import EngagementHighlightsSignalsCard from "./EngagementHighlightsSignalsCard";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ✅ RATING SCALE + COLORS (NOW INCLUDES C) */
const RATING_SCALE = {
  C: 0,
  "B-": 1,
  B: 2,
  "B+": 3,
  "A-": 4,
  A: 5,
  "A+": 6,
};

const RATING_LABELS = {
  0: "C",
  1: "B-",
  2: "B",
  3: "B+",
  4: "A-",
  5: "A",
  6: "A+",
};

const RATING_COLORS = {
  C: "#991b1b",
  "B-": "#ef4444",
  B: "#f97316",
  "B+": "#f59e0b",
  "A-": "#84cc16",
  A: "#22c55e",
  "A+": "#06b6d4",
};

const ClientEngagementDashboard = ({ data }) => {
  const requestId =
    data?.request_id ||
    data?.requestId ||
    data?.api_request_id ||
    data?.apiRequestId ||
    "";

  const userEmail = process.env.REACT_APP_USER;

  const theme = useTheme();
  const primaryStart = "#003366";
  const primaryEnd = "#004080";

  const mv = data?.meetings_view || {};
  const consultantView = data?.consultant_view || {};
  const consultantRatings = consultantView?.consultant_ratings || [];
  const consultantCommentary = consultantView?.consultant_commentary || [];

  const entityId =
    data?.client_id || data?.client_360_overview?.core?.client_name || "";

  /**
   * ✅ Timeline Data:
   * 1) Primary: meeting_counts_by_month (YYYY-MM -> count)
   * 2) Fallback: highlights[] (existing date bucketing)
   */
  const timelineData = useMemo(() => {
    const countsByMonth = mv?.meeting_counts_by_month;

    // --- Primary path ---
    if (countsByMonth && Object.keys(countsByMonth).length > 0) {
      const arr = Object.entries(countsByMonth)
        .map(([ym, count]) => {
          const [yStr, mStr] = String(ym).split("-");
          const year = Number(yStr);
          const monthIdx = Number(mStr) - 1;

          if (
            !year ||
            Number.isNaN(monthIdx) ||
            monthIdx < 0 ||
            monthIdx > 11
          ) {
            return null;
          }

          return {
            label: `${MONTH_LABELS[monthIdx]} ${year}`,
            dateObj: new Date(year, monthIdx, 1),
            count: Number(count) || 0,
          };
        })
        .filter(Boolean);

      arr.sort((a, b) => a.dateObj - b.dateObj);
      return arr.map(({ label, count }) => ({ label, count }));
    }

    // --- Fallback path (highlights) ---
    const highlights = mv?.highlights || [];
    if (!highlights.length) return [];

    const buckets = {};
    highlights.forEach((h) => {
      const raw = h?.date;
      if (!raw) return;

      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;

      const year = d.getFullYear();
      const monthIdx = d.getMonth();
      const monthLabel = MONTH_LABELS[monthIdx] || "";
      const key = `${year}-${monthIdx}`;

      if (!buckets[key]) {
        buckets[key] = {
          label: `${monthLabel} ${year}`,
          dateObj: new Date(year, monthIdx, 1),
          count: 0,
        };
      }
      buckets[key].count += 1;
    });

    const arr = Object.values(buckets);
    arr.sort((a, b) => a.dateObj - b.dateObj);
    return arr.map(({ label, count }) => ({ label, count }));
  }, [mv]);

  const highlights = mv?.highlights || [];
  const upcoming = mv?.upcoming_meetings || [];
  const topRisks = mv?.talking_points?.top_risks || [];
  const topOpps = mv?.talking_points?.top_opportunities || [];

  // (kept for backward compatibility if any other consumers use it)
  const scatterByRating = useMemo(() => {
    const groups = {};
    consultantRatings.forEach((row, idx) => {
      const pg =
        row?.product_group ||
        row?.key_product_group ||
        row?.keyProductGroup ||
        row?.["product_group"] ||
        `Product ${idx + 1}`;

      const ratingLabel = row?.rating || "";
      const y = RATING_SCALE[ratingLabel];

      // ✅ IMPORTANT: allow y=0 for "C"
      if (y === null || y === undefined) return;

      if (!groups[ratingLabel]) groups[ratingLabel] = [];
      groups[ratingLabel].push({
        x: idx,
        y,
        productGroup: pg,
        ratingLabel,
        notes: row?.notes || "",
      });
    });

    return groups;
  }, [consultantRatings]);

  const productGroupTick = (value) => {
    const idx = Number(value);
    const pg =
      consultantRatings?.[idx]?.product_group ||
      consultantRatings?.[idx]?.key_product_group ||
      consultantRatings?.[idx]?.keyProductGroup ||
      consultantRatings?.[idx]?.["product_group"];

    if (!pg) return "";
    return pg.length > 14 ? `${pg.slice(0, 14)}…` : pg;
  };

  return (
    <Box sx={{ pt: 10, pb: 4, display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          maxWidth: "96vw",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* TOP ROW — TALKING POINTS + UPCOMING */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 4,
            alignItems: "stretch",
          }}
        >
          <TalkingPointsCard
            theme={theme}
            topRisks={topRisks}
            topOpps={topOpps}
            entityId={entityId}
            AiSectionFeedback={AiSectionFeedback}
            requestId={requestId}
            uiSectionKey="talking_points"
            userEmail={userEmail}
          />

          <UpcomingMeetingsCard theme={theme} upcoming={upcoming} />
        </Box>

        {/* ROW 2 — CONSULTANT SCATTER + TIMELINE + COMMENTARY */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: 4,
            alignItems: "stretch",
          }}
        >
          <ConsultantIntelligenceScatterCard
            theme={theme}
            consultantRatings={consultantRatings}
            scatterByRating={scatterByRating}
            productGroupTick={productGroupTick}
            ratingScale={RATING_SCALE}
            ratingLabels={RATING_LABELS}
            ratingColors={RATING_COLORS}
            entityId={entityId}
            AiSectionFeedback={AiSectionFeedback}
            requestId={requestId}
            uiSectionKey="consultant_intelligence"
            userEmail={userEmail}
          />

          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}
          >
            <InteractionTimelineCard
              theme={theme}
              timelineData={timelineData}
              primaryStart={primaryStart}
              primaryEnd={primaryEnd}
            />

            <ConsultantCommentaryCard
              theme={theme}
              consultantCommentary={consultantCommentary}
            />
          </Box>
        </Box>

        {/* ROW 3 — MERGED CARD */}
        <EngagementHighlightsSignalsCard
          theme={theme}
          highlights={highlights}
          topRisks={topRisks}
          topOpps={topOpps}
          entityId={entityId}
          AiSectionFeedback={AiSectionFeedback}
          requestId={requestId}
          uiSectionKey="highlights"
          userEmail={userEmail}
        />
      </Box>
    </Box>
  );
};

export default ClientEngagementDashboard;
